import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import CertificationService from "../Service/CertificationService";

const CertificationDetailsPage = () => {
  const initialCert = {
    id: "",
    certificationId: "",
    certificateIssuer: "",
    skillName: "",
    certificationName: "",
    dateOfIssue: "",
    validUpTo: "",
  };

  const location = useLocation();
  const { user } = location.state || {};

  const [certs, setCerts] = useState([]);
  const [newCert, setNewCert] = useState({ ...initialCert });
  const [errorMsg, setErrorMsg] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const [editFieldErrors, setEditFieldErrors] = useState({});

  const formatDateForInput = (dateStr) => {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    if (isNaN(d)) return "";
    return d.toISOString().split("T")[0];
  };

  const formatDateForDisplay = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  useEffect(() => {
    const fetchCertifications = async () => {
      if (!user?.userId) return;
      try {
        const response = await CertificationService.getCertificationsByUserId(user.userId);
        const fetchedCerts = response.map(cert => ({
          savedData: cert,
          isEditing: false,
          editData: {
            id: cert.id,
            certificationId: cert.certificationId,
            certificateIssuer: cert.certificateIssuer,
            skillName: cert.skillName,
            certificationName: cert.certificationName,
            dateOfIssue: formatDateForInput(cert.dateOfIssue),
            validUpTo: formatDateForInput(cert.validUpTo),
          },
        }));
        setCerts(fetchedCerts);
      } catch (err) {
        console.error("Error fetching certifications:", err);
      }
    };
    fetchCertifications();
  }, [user]);

  const handleChange = (e, index = null, isNew = false) => {
    const { name, value } = e.target;
    if (isNew) {
      setNewCert(prev => ({ ...prev, [name]: value }));
    } else {
      setCerts(prev => {
        const updated = [...prev];
        updated[index].editData[name] = value;
        return updated;
      });
    }
  };

  const validateCertFields = (cert) => {
    const errors = {};
    if (!cert.certificationId.trim()) errors.certificationId = true;
    if (!cert.certificationName.trim()) errors.certificationName = true;
    if (!cert.certificateIssuer.trim()) errors.certificateIssuer = true;
    if (!cert.skillName.trim()) errors.skillName = true;
    if (!cert.dateOfIssue) errors.dateOfIssue = true;
    return errors;
  };

  const handleAdd = async () => {
    const errors = validateCertFields(newCert);
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      setSuccessMsg("");
      return;
    }

    try {
      const payload = {
        ...newCert,
        userId: user?.userId,
        userName: user?.userName,
      };
      const response = await CertificationService.addCertification(payload);
      const certEntry = {
        savedData: response,
        isEditing: false,
        editData: {
          id: response.id,
          certificationId: response.certificationId,
          certificateIssuer: response.certificateIssuer,
          skillName: response.skillName,
          certificationName: response.certificationName,
          dateOfIssue: formatDateForInput(response.dateOfIssue),
          validUpTo: formatDateForInput(response.validUpTo),
        },
      };
      setCerts(prev => [...prev, certEntry]);
      setNewCert({ ...initialCert });
      setErrorMsg("");
      setFieldErrors({});
    } catch (error) {
      console.error("Error while adding certification:", error);
      setErrorMsg("Failed to add certification.");
      setSuccessMsg("");
    }
  };

  const handleEdit = (index) => {
    const updated = [...certs];
    updated[index].isEditing = true;
    setCerts(updated);
    setSuccessMsg("");
  };

  const handleUpdate = async (index) => {
    const certToUpdate = certs[index];
    const errors = validateCertFields(certToUpdate.editData);
    if (Object.keys(errors).length > 0) {
      setEditFieldErrors(prev => ({ ...prev, [index]: errors }));
      return;
    }

    const payload = {
      ...certToUpdate.editData,
      userId: user?.userId,
      userName: user?.userName,
    };

    try {
      const response = await CertificationService.updateCertification(payload);
      const updated = [...certs];
      updated[index] = {
        savedData: response,
        isEditing: false,
        editData: {
          id: response.id,
          certificationId: response.certificationId,
          certificateIssuer: response.certificateIssuer,
          skillName: response.skillName,
          certificationName: response.certificationName,
          dateOfIssue: formatDateForInput(response.dateOfIssue),
          validUpTo: formatDateForInput(response.validUpTo),
        },
      };
      setCerts(updated);
      setErrorMsg("");
      setEditFieldErrors(prev => {
        const { [index]: _, ...rest } = prev;
        return rest;
      });
    } catch (error) {
      console.error("Error updating certification:", error);
      setErrorMsg("Failed to update certification.");
      setSuccessMsg("");
    }
  };

  const handleCancel = (index) => {
    const updated = [...certs];
    const saved = updated[index].savedData;
    updated[index] = {
      ...updated[index],
      isEditing: false,
      editData: {
        id: saved.id,
        certificationId: saved.certificationId,
        certificateIssuer: saved.certificateIssuer,
        skillName: saved.skillName,
        certificationName: saved.certificationName,
        dateOfIssue: formatDateForInput(saved.dateOfIssue),
        validUpTo: formatDateForInput(saved.validUpTo),
      },
    };
    setCerts(updated);
    setEditFieldErrors(prev => {
      const { [index]: _, ...rest } = prev;
      return rest;
    });
  };

  const handleDelete = async (index) => {
    const id = certs[index].savedData.id;
    const confirmed = window.confirm(`Are you sure you want to delete the certification "${certs[index].savedData.certificationName}"?`);
    if (!confirmed) return;

    try {
      await CertificationService.deleteCertification(id);
      const updated = [...certs];
      updated.splice(index, 1);
      setCerts(updated);
      setErrorMsg("");
    } catch (error) {
      console.error("Error deleting certification:", error);
      setErrorMsg("Failed to delete certification.");
      setSuccessMsg("");
    }
  };

  const toTitleCase = (str) => {
    return str
      .replace(/([A-Z])/g, " $1")
      .replace(/^./, (s) => s.toUpperCase())
      .replace(/\b\w/g, (s) => s.toUpperCase());
  };

  return (
    <div className="border border-blue-900 p-6 max-w-7xl mx-auto font-sans">
      <h2 className="text-2xl font-bold text-center mb-6">Certification Details</h2>

      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 mb-6">
        {["certificationId", "certificateIssuer", "skillName", "certificationName", "dateOfIssue", "validUpTo"].map((field) => (
          <label key={field} className="flex flex-col">
            <span className="mb-1">
              {toTitleCase(field)}
              {fieldErrors[field] && <span className="text-red-500"> *</span>}:
            </span>
            <input
              type={["dateOfIssue", "validUpTo"].includes(field) ? "date" : "text"}
              name={field}
              value={newCert[field]}
              onChange={(e) => handleChange(e, null, true)}
              className={`p-2 rounded border ${fieldErrors[field] ? "border-red-500" : "border-gray-300"}`}
            />
          </label>
        ))}

        <div className="col-span-full text-center">
          <button onClick={handleAdd} className="bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700">Add Certificate</button>
        </div>

        {successMsg && <div className="col-span-full text-green-600 text-center">{successMsg}</div>}
        {errorMsg && <div className="col-span-full text-red-600 text-center">{errorMsg}</div>}
      </div>

      {certs.length > 0 && (
        <div className="overflow-x-auto">
          <table className="min-w-full table-auto border-collapse">
            <thead>
              <tr className="bg-gray-200">
                <th className="py-1 px-1 text-sm">Certificate Id</th>
                <th className="py-1 px-1 text-sm">Certificate Issuer</th>
                <th className="py-1 px-1 text-sm">Skill Name</th>
                <th className="py-1 px-1 text-sm">Certification Name</th>
                <th className="py-1 px-1 text-sm">Date of Issue</th>
                <th className="py-1 px-1 text-sm">Valid Up To</th>
                <th className="py-1 px-1 text-sm">Actions</th>
              </tr>
            </thead>
            <tbody>
              {certs.map((cert, index) => (
                <tr key={index} className="text-center border-t">
                  {cert.isEditing ? (
                    <>
                      {["certificationId", "certificateIssuer", "skillName", "certificationName", "dateOfIssue", "validUpTo"].map((field, idx) => (
                        <td key={idx} className="p-2">
                          <input
                            type={["dateOfIssue", "validUpTo"].includes(field) ? "date" : "text"}
                            name={field}
                            value={cert.editData[field] || ""}
                            onChange={(e) => handleChange(e, index)}
                            className={`p-1 rounded border w-full ${editFieldErrors[index]?.[field] ? "border-red-500" : "border-gray-300"}`}
                          />
                        </td>
                      ))}


                    <td>
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleUpdate(index)}
                          className="px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600"
                        >
                          Update
                        </button>
                        <button
                          onClick={() => handleCancel(index)}
                          className="px-3 py-1 bg-gray-500 text-white rounded hover:bg-gray-600"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={() => handleDelete(index)}
                          className="px-3 py-1 bg-red-500 text-white rounded hover:bg-red-600"
                        >
                          Delete
                        </button>
                      </div>
                    </td>

                    </>
                  ) : (
                    <>
                      <td className="py-1 px-1 text-sm text-left">{cert.savedData.certificationId}</td>
                      <td className="py-1 px-1 text-sm text-left">{cert.savedData.certificateIssuer}</td>
                      <td className="py-1 px-1 text-sm text-left">{cert.savedData.skillName}</td>
                      <td className="py-1 px-1 text-sm text-left">{cert.savedData.certificationName}</td>
                      <td className="py-1 px-1 text-sm text-left">{formatDateForDisplay(cert.savedData.dateOfIssue)}</td>
                      <td className="py-1 px-1 text-sm text-left">{formatDateForDisplay(cert.savedData.validUpTo)}</td>
                     

                      <td>
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleEdit(index)}
                          className="px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600"
                        >
                          Update
                        </button>
                        <button
                          onClick={() => handleDelete(index)}
                          className="px-3 py-1 bg-red-500 text-white rounded hover:bg-red-600"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default CertificationDetailsPage;